import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import { Button } from "src/components/ui/Button";
import { Avatar, AvatarFallback, AvatarImage } from "src/components/ui/Avatar";
import { supabase } from "src/lib/supabaseClient";
import {
  ArrowLeft,
  Mail,
  GraduationCap,
  Award,
  Calendar,
  Clock,
  Edit,
  Trash2,
  Star,
  FileText,
  Eye,
  Download,
  UserCheck,
  UserX,
  BadgeCheck,
  Briefcase,
  Users,
  MapPin,
  Check,
  Zap,
  AlertCircle,
} from "lucide-react";
import { useToast } from "src/components/ui/UseToast";
import { calculateTutorStatus } from "src/utils/tutorStatusUtils";
import { parseStorageUrl } from "src/utils/storageUtils";

interface EducationEntry {
  degree?: string;
  field?: string;
  institution?: string;
  year?: string | number;
  gpa?: string | number;
}

interface AvailabilitySlot {
  day_of_week: number;
  start_time: string;
  end_time: string;
  status: string;
}

interface TutorDetails {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  profilePictureUrl?: string;
  specializations: string[];
  experience: number;
  education: string;
  educationEntries: EducationEntry[];
  bio: string;
  hourlyRate: number;
  rating: number;
  totalSessions: number;
  totalStudents: number;
  status: string;
  statusLabel: string;
  certifications: string;
  certificationsList: string[];
  availability: string;
  availabilitySlots: AvailabilitySlot[];
  cvFilePath?: string;
  createdAt: string;
  lastActive?: string;
}

const CV_BUCKET = "tutor-uploads";

const toTextArray = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item?.name || item?.subject || "")))
      .filter(Boolean);
  }

  if (typeof value !== "string" || !value.trim()) return [];

  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) return toTextArray(parsed);
  } catch {
    // Fall back to comma-separated values below.
  }

  return value.split(",").map((item) => item.trim()).filter(Boolean);
};

const parseEducationEntries = (education: unknown): EducationEntry[] => {
  if (Array.isArray(education)) {
    return education
      .map((entry) => {
        if (typeof entry === "string") {
          return { degree: entry };
        }
        return {
          degree: entry?.degree || entry?.level,
          field: entry?.field,
          institution: entry?.institution,
          year: entry?.year_of_completion || entry?.year,
          gpa: entry?.gpa || entry?.gpa_total,
        };
      })
      .filter((e) => e.degree || e.field || e.institution);
  }

  if (typeof education === "string" && education.trim()) {
    try {
      const parsed = JSON.parse(education);
      if (Array.isArray(parsed)) return parseEducationEntries(parsed);
    } catch {
      return [{ degree: education }];
    }
  }

  return [];
};

const formatEducation = (education: unknown): string => {
  if (typeof education === "string" && education.trim()) return education;
  if (!Array.isArray(education) || education.length === 0) return "Not specified";

  return education
    .map((entry) => {
      if (typeof entry === "string") return entry;

      const degree = entry?.degree || entry?.level;
      const field = entry?.field;
      const institution = entry?.institution;
      const year = entry?.year_of_completion || entry?.year;

      return [degree, field, institution ? `from ${institution}` : "", year ? `(${year})` : ""]
        .filter(Boolean)
        .join(" ");
    })
    .filter(Boolean)
    .join("; ") || "Not specified";
};

const calculateExperienceYears = (experience: unknown): number => {
  if (typeof experience === "number") return experience;
  if (typeof experience === "string") return Number(experience) || 0;
  if (!Array.isArray(experience)) return 0;

  return experience.reduce((total, entry) => {
    const years = Number(entry?.years || entry?.duration_years || entry?.experience_years || 0);
    return total + (Number.isFinite(years) ? years : 0);
  }, 0);
};

const getCertificationsList = (rawTutor: any): string[] => {
  const values = [
    ...toTextArray(rawTutor.certifications),
    ...toTextArray(rawTutor.achievements),
    ...toTextArray(rawTutor.expertise),
  ];

  return Array.from(new Set(values));
};

const formatCertifications = (rawTutor: any): string => {
  const list = getCertificationsList(rawTutor);
  return list.length > 0 ? list.join(", ") : "Not specified";
};

const formatAvailability = (slots: any[]): string => {
  if (!slots.length) return "Not specified";

  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  return slots
    .map((slot) => {
      const day = dayNames[slot.day_of_week] || `Day ${slot.day_of_week}`;
      return `${day}: ${slot.start_time.slice(0, 5)} - ${slot.end_time.slice(0, 5)}`;
    })
    .join(", ");
};

const getStorageFilePath = (cvFilePath: string): { bucket: string; filePath: string } | null => {
  const parsedUrl = parseStorageUrl(cvFilePath);
  if (parsedUrl) return parsedUrl;

  const normalizedPath = cvFilePath.trim().replace(/^\/+/, "");
  if (!normalizedPath) return null;

  if (normalizedPath.startsWith(`${CV_BUCKET}/`)) {
    return {
      bucket: CV_BUCKET,
      filePath: normalizedPath.slice(CV_BUCKET.length + 1),
    };
  }

  return {
    bucket: CV_BUCKET,
    filePath: normalizedPath,
  };
};

const getFileName = (cvFilePath: string): string => {
  try {
    const path = cvFilePath.startsWith("http") ? new URL(cvFilePath).pathname : cvFilePath;
    const fileName = path.split("/").filter(Boolean).pop();
    return fileName ? decodeURIComponent(fileName) : "Tutor CV";
  } catch {
    const fileName = cvFilePath.split("/").filter(Boolean).pop();
    return fileName || "Tutor CV";
  }
};

const getStatusUpdates = (status: string): { verification_status: string; is_live: boolean } => {
  switch (status) {
    case "active":
      return { verification_status: "approved", is_live: true };
    case "approved":
      return { verification_status: "approved", is_live: false };
    case "under_review":
      return { verification_status: "under_review", is_live: false };
    case "rejected":
      return { verification_status: "rejected", is_live: false };
    case "submitted":
    default:
      return { verification_status: "submitted", is_live: false };
  }
};

const getStatusBadgeStyle = (status: string) => {
  switch (status) {
    case "active":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "approved":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "under_review":
      return "bg-amber-50 text-amber-800 border-amber-200";
    case "submitted":
      return "bg-slate-100 text-slate-700 border-slate-200";
    case "rejected":
      return "bg-rose-50 text-rose-700 border-rose-200";
    default:
      return "bg-slate-100 text-slate-700 border-slate-200";
  }
};

const calculateWeeklyHours = (slots: AvailabilitySlot[]): number => {
  return Math.round(
    slots.reduce((total, slot) => {
      const [startH, startM] = (slot.start_time || "0:0").split(":").map(Number);
      const [endH, endM] = (slot.end_time || "0:0").split(":").map(Number);
      const startMinutes = (startH || 0) * 60 + (startM || 0);
      const endMinutes = (endH || 0) * 60 + (endM || 0);
      const diff = Math.max(0, endMinutes - startMinutes);
      return total + diff / 60;
    }, 0)
  );
};

const ViewTutor = () => {
  const navigate = useNavigate();
  const { tutorId } = useParams<{ tutorId: string }>();
  const [searchParams] = useSearchParams();
  const returnToApprovalQueue = searchParams.get("from") === "approval-queue";
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();
  const { toast } = useToast();

  const [tutor, setTutor] = useState<TutorDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<{ message: string; code?: string } | null>(null);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (!tutorId) {
      navigate("/admin/tutors/list", { replace: true });
      return;
    }

    fetchTutor(tutorId);
  }, [navigate, tutorId]);

  useEffect(() => {
    setTitle("Tutor Profile");
    setDescription("Tutor Verification Dossier & Management Console");
    setActions(undefined);

    return () => reset();
  }, [setTitle, setDescription, setActions, reset]);

  const fetchTutor = async (id: string) => {
    try {
      setLoading(true);
      setFetchError(null);
      const { data, error } = await supabase
        .from("tutors")
        .select(`
          id,
          education,
          hourly_rate,
          subjects_taught,
          experience,
          bio,
          verification_status,
          rating,
          created_at,
          updated_at,
          is_live,
          cv_file_path,
          achievements,
          expertise,
          profile:profiles!tutors_id_fkey (
            first_name,
            last_name,
            email,
            profile_picture_url
          )
        `)
        .eq("id", id)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setTutor(null);
        return;
      }

      const [{ data: sessionsData, error: sessionsError }, { data: availabilityData, error: availabilityError }] =
        await Promise.all([
          supabase.from("sessions").select("student_id").eq("tutor_id", id),
          supabase
            .from("tutor_availability")
            .select("day_of_week, start_time, end_time, status")
            .eq("tutor_id", id)
            .order("day_of_week", { ascending: true })
            .order("start_time", { ascending: true }),
        ]);

      if (sessionsError) {
        console.warn("Failed to fetch tutor sessions:", sessionsError);
      }

      if (availabilityError) {
        console.warn("Failed to fetch tutor availability:", availabilityError);
      }

      const profile = Array.isArray(data.profile) ? data.profile[0] : data.profile;
      const subjects = toTextArray(data.subjects_taught);
      const status = calculateTutorStatus(data.verification_status, data.is_live);
      const uniqueStudents = new Set((sessionsData || []).map((session) => session.student_id).filter(Boolean));
      const slots: AvailabilitySlot[] = (availabilityData || []).map((slot: any) => ({
        day_of_week: Number(slot.day_of_week),
        start_time: String(slot.start_time || ""),
        end_time: String(slot.end_time || ""),
        status: String(slot.status || "available"),
      }));

      setTutor({
        id: data.id,
        firstName: profile?.first_name || "",
        lastName: profile?.last_name || "",
        email: profile?.email || "No email",
        profilePictureUrl: profile?.profile_picture_url || undefined,
        specializations: subjects.length > 0 ? subjects : ["General Tutoring"],
        experience: calculateExperienceYears(data.experience),
        education: formatEducation(data.education),
        educationEntries: parseEducationEntries(data.education),
        bio: data.bio || "No teaching philosophy or biography provided.",
        hourlyRate: Number(data.hourly_rate) || 0,
        rating: Number(data.rating) || 5.0,
        totalSessions: sessionsData?.length || 0,
        totalStudents: uniqueStudents.size,
        status: status.status,
        statusLabel: status.displayValue,
        certifications: formatCertifications(data),
        certificationsList: getCertificationsList(data),
        availability: formatAvailability(slots),
        availabilitySlots: slots,
        cvFilePath: data.cv_file_path || undefined,
        createdAt: data.created_at,
        lastActive: data.updated_at,
      });
    } catch (error: any) {
      console.error("Failed to fetch tutor details:", error);
      const errorMsg = error?.message || "Failed to fetch tutor details. Please try again.";
      setFetchError({
        message: errorMsg,
        code: error?.code,
      });
      toast({
        title: "Error Loading Tutor",
        description: errorMsg,
        variant: "destructive",
      });
      setTutor(null);
    } finally {
      setLoading(false);
    }
  };

  const getCVUrl = async (download = false): Promise<string> => {
    if (!tutor?.cvFilePath) throw new Error("CV file is not available");

    const storageFile = getStorageFilePath(tutor.cvFilePath);
    if (!storageFile) return tutor.cvFilePath;

    const { data, error } = await supabase.storage
      .from(storageFile.bucket)
      .createSignedUrl(storageFile.filePath, 300, download ? { download: true } : undefined);

    if (error || !data?.signedUrl) {
      throw error || new Error("Unable to create CV link");
    }

    return data.signedUrl;
  };

  const handleViewCV = async () => {
    const viewWindow = window.open("", "_blank", "noopener,noreferrer");

    try {
      const url = await getCVUrl(false);
      if (viewWindow) {
        viewWindow.location.href = url;
      } else {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      viewWindow?.close();
      console.error("Failed to open CV:", error);
      toast({
        title: "Error",
        description: "Failed to open the tutor CV. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadCV = async () => {
    try {
      const url = await getCVUrl(true);
      const link = document.createElement("a");
      link.href = url;
      link.download = tutor?.cvFilePath ? getFileName(tutor.cvFilePath) : "Tutor CV";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Failed to download CV:", error);
      toast({
        title: "Error",
        description: "Failed to download the tutor CV. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDeleteTutor = async () => {
    if (!confirm("Are you sure you want to delete this tutor? This action cannot be undone.")) return;

    try {
      if (!tutorId) throw new Error("Tutor ID is required");

      const { error } = await supabase.from("tutors").delete().eq("id", tutorId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Tutor deleted successfully!",
        variant: "default",
      });

      navigate("/admin/tutors/list");
    } catch (error) {
      console.error("Failed to delete tutor:", error);
      toast({
        title: "Error",
        description: "Failed to delete tutor. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleUpdateStatus = async (targetStatus: string) => {
    try {
      if (!tutorId) throw new Error("Tutor ID is required");

      const statusUpdates = getStatusUpdates(targetStatus);
      setUpdatingStatus(true);

      const { error } = await supabase
        .from("tutors")
        .update({
          ...statusUpdates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", tutorId);

      if (error) throw error;

      const updatedStatus = calculateTutorStatus(statusUpdates.verification_status, statusUpdates.is_live);
      setTutor((prev) =>
        prev
          ? {
              ...prev,
              status: updatedStatus.status,
              statusLabel: updatedStatus.displayValue,
              lastActive: new Date().toISOString(),
            }
          : prev
      );

      toast({
        title: "Status Updated",
        description: `Tutor status set to ${updatedStatus.displayValue}.`,
        variant: "default",
      });
    } catch (error) {
      console.error("Failed to update tutor status:", error);
      toast({
        title: "Error",
        description: "Failed to update tutor status. Please try again.",
        variant: "destructive",
      });
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDisplayDate = (date?: string): string => {
    if (!date) return "Not available";

    return new Date(date).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFullName = () => `${tutor?.firstName || ""} ${tutor?.lastName || ""}`.trim() || "Tutor";

  const getTutorInitials = () => {
    const names = getFullName().split(" ").filter(Boolean);
    return names.length > 1
      ? `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase()
      : getFullName().slice(0, 2).toUpperCase();
  };

  const getTutorHeadline = () => {
    const subjects = tutor?.specializations.filter((s) => s !== "Not specified" && s !== "General Tutoring") || [];
    if (subjects.length >= 2) return `${subjects[0]} & ${subjects[1]} Specialist`;
    if (subjects.length === 1) return `${subjects[0]} Educator`;
    return "Verified rfLearn Educator";
  };

  // Schedule matrix helpers
  const daysOfWeek = [
    { key: 1, label: "Mon" },
    { key: 2, label: "Tue" },
    { key: 3, label: "Wed" },
    { key: 4, label: "Thu" },
    { key: 5, label: "Fri" },
    { key: 6, label: "Sat" },
    { key: 0, label: "Sun" },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-violet-600 border-t-transparent"></div>
          <span className="text-xs font-medium text-slate-500">Loading tutor verification dossier...</span>
        </div>
      </div>
    );
  }

  // Error state: database/query/network failure
  if (fetchError) {
    return (
      <div className="max-w-lg mx-auto my-12 bg-white rounded-xl border border-rose-200 p-8 text-center shadow-xs">
        <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4 border border-rose-100">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">Failed to Load Tutor Details</h2>
        <p className="text-sm text-slate-600 mb-3">
          An error occurred while communicating with the database.
        </p>
        <div className="p-3 mb-6 rounded-lg bg-slate-50 border border-slate-200 text-left text-xs font-mono text-slate-700 break-words">
          {fetchError.code && (
            <span className="text-slate-400 block mb-1 font-sans font-semibold text-[11px] uppercase tracking-wider">
              Error Code: {fetchError.code}
            </span>
          )}
          {fetchError.message}
        </div>
        <div className="flex items-center justify-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate("/admin/tutors/list")}
            className="text-xs font-semibold"
          >
            Back to Directory
          </Button>
          <Button
            onClick={() => tutorId && fetchTutor(tutorId)}
            className="text-xs font-semibold bg-violet-600 hover:bg-violet-700 text-white"
          >
            Retry Request
          </Button>
        </div>
      </div>
    );
  }

  // 404 Not Found state: query succeeded but tutor record doesn't exist
  if (!tutor) {
    return (
      <div className="max-w-md mx-auto my-12 bg-white rounded-xl border border-slate-200 p-8 text-center shadow-xs">
        <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center mx-auto mb-4">
          <AlertCircle className="w-6 h-6" />
        </div>
        <h2 className="text-lg font-bold text-slate-900 mb-1">Tutor Not Found</h2>
        <p className="text-sm text-slate-500 mb-6">
          The tutor record with ID <code className="text-xs font-mono bg-slate-100 px-1 py-0.5 rounded">{tutorId}</code> could not be located in the database.
        </p>
        <Button onClick={() => navigate("/admin/tutors/list")} className="w-full text-xs font-semibold">
          Back to Tutors Directory
        </Button>
      </div>
    );
  }

  const weeklyHours = calculateWeeklyHours(tutor.availabilitySlots);

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-12">
      {/* Top Context Nav & Action Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-1">
        <div className="flex items-center gap-2.5 text-xs">
          <button
            onClick={() =>
              navigate(returnToApprovalQueue ? "/admin/tutors/approval-queue" : "/admin/tutors/list")
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition font-medium shadow-2xs"
          >
            <ArrowLeft className="w-3.5 h-3.5" />
            {returnToApprovalQueue ? "Approval Queue" : "Tutors Directory"}
          </button>
          <span className="text-slate-300">/</span>
          <span className="font-semibold text-slate-700">Tutor Verification Dossier</span>
          <span className="text-slate-300">/</span>
          <span className="font-mono text-slate-400 text-[11px] truncate max-w-[120px]">{tutor.id}</span>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/tutors/edit/${tutorId}`)}
            className="text-xs font-semibold text-slate-700 border-slate-200 bg-white hover:bg-slate-50 flex items-center gap-1.5 shadow-2xs"
          >
            <Edit className="w-3.5 h-3.5 text-slate-500" />
            Edit Profile
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/admin/tutors/delete/${tutorId}`)}
            className="text-xs font-semibold text-rose-600 border-rose-200 bg-white hover:bg-rose-50 flex items-center gap-1.5 shadow-2xs"
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete
          </Button>
        </div>
      </div>

      {/* Main Layout Grid (Option A: 8 cols Dossier Context / 4 cols Administrative Command Deck) */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        {/* Left Column: Comprehensive Profile & Evidence (8 cols) */}
        <div className="lg:col-span-8 space-y-6">
          {/* Profile Header Card */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-xs">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <div className="relative shrink-0">
                <Avatar className="h-20 w-20 rounded-xl border border-slate-200 shadow-sm sm:h-22 sm:w-22">
                  <AvatarImage
                    src={tutor.profilePictureUrl}
                    alt={getFullName()}
                    className="object-cover rounded-xl"
                  />
                  <AvatarFallback className="rounded-xl bg-gradient-to-tr from-violet-600 to-indigo-700 font-bold text-xl text-white">
                    {getTutorInitials()}
                  </AvatarFallback>
                </Avatar>
                <span
                  className="absolute -bottom-1 -right-1 p-0.5 bg-white rounded-full shadow-2xs"
                  title="Profile Verified"
                >
                  <span className="block w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-white"></span>
                </span>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                        {getFullName()}
                      </h1>
                      <BadgeCheck className="h-5 w-5 shrink-0 text-violet-600 fill-violet-100" />
                    </div>
                    <p className="text-sm font-medium text-slate-500 mt-0.5">{getTutorHeadline()}</p>
                  </div>

                  {/* Quick Stats Pills */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200 font-medium text-slate-700">
                      <strong className="text-slate-900 font-bold">{tutor.experience}</strong> yrs exp
                    </span>
                    <span className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 font-medium text-amber-800 flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />
                      <strong>{tutor.rating.toFixed(1)}</strong>
                      <span className="text-amber-600/80 text-[11px]">
                        ({tutor.totalSessions > 0 ? `${tutor.totalSessions} sessions` : "New"})
                      </span>
                    </span>
                  </div>
                </div>

                {/* Contact & Meta Strip */}
                <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-slate-600">
                  <div className="flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-slate-400" />
                    <span className="font-mono text-slate-800">{tutor.email}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span>Indian Standard Time (IST, UTC +05:30)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>Joined {formatDisplayDate(tutor.createdAt)}</span>
                  </div>
                </div>

                {/* Curriculum Subjects & Specializations */}
                <div className="mt-4 flex flex-wrap items-center gap-1.5">
                  <span className="text-xs font-semibold text-slate-500 mr-1">Curriculum Specializations:</span>
                  {tutor.specializations.map((spec) => (
                    <span
                      key={spec}
                      className="px-2.5 py-1 rounded-md bg-slate-100 text-slate-800 text-xs font-medium border border-slate-200"
                    >
                      {spec}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Academic & Professional Credentials Card */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
              <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-violet-600" />
                Academic Credentials & Teaching Pedigree
              </h2>
              <span className="text-xs text-slate-500 font-medium">Verified by rfLearn Admin</span>
            </div>

            <div className="p-6 space-y-6">
              {/* Formal Degrees */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
                  Academic Background
                </h3>
                {tutor.educationEntries.length > 0 ? (
                  <div className="space-y-3">
                    {tutor.educationEntries.map((edu, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-3 p-3.5 rounded-lg border border-slate-100 bg-slate-50/60"
                      >
                        <div className="w-9 h-9 rounded-lg bg-violet-100 text-violet-700 font-bold text-xs flex items-center justify-center shrink-0">
                          {edu.degree ? edu.degree.slice(0, 3).toUpperCase() : "DEG"}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <h4 className="text-sm font-semibold text-slate-900 truncate">
                              {edu.degree || "Degree"} {edu.field ? `in ${edu.field}` : ""}
                            </h4>
                            {edu.year && (
                              <span className="text-xs font-mono text-slate-500 shrink-0">{edu.year}</span>
                            )}
                          </div>
                          <p className="text-xs text-slate-600 mt-0.5">
                            {edu.institution || "Institution not specified"}
                            {edu.gpa ? ` • GPA: ${edu.gpa}` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3.5 rounded-lg border border-slate-100 bg-slate-50/60 text-sm text-slate-800">
                    {tutor.education}
                  </div>
                )}
              </div>

              {/* Teaching Philosophy & Bio */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Teaching Philosophy & Bio
                </h3>
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200/80 text-sm text-slate-700 leading-relaxed">
                  "{tutor.bio}"
                </div>
              </div>

              {/* Verified Honors & Certifications */}
              <div>
                <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">
                  Verified Honors & Certifications
                </h3>
                {tutor.certificationsList.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {tutor.certificationsList.map((cert, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-xs font-medium text-slate-800 shadow-2xs"
                      >
                        <Award className="w-3.5 h-3.5 text-amber-500" />
                        {cert}
                      </span>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No additional certifications provided.</p>
                )}
              </div>
            </div>
          </div>

          {/* Weekly Availability Schedule Matrix */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-xs overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <h2 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
                  <Clock className="w-4 h-4 text-violet-600" />
                  Weekly Availability Grid
                </h2>
                <p className="text-xs text-slate-500 mt-0.5">
                  All slots displayed in Indian Standard Time (IST, UTC+05:30)
                </p>
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 self-start sm:self-auto">
                {weeklyHours > 0 ? `${weeklyHours} Active Hours / Week` : "Flexible Availability"}
              </span>
            </div>

            <div className="p-6">
              {tutor.availabilitySlots.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-7 gap-2.5 text-center">
                  {daysOfWeek.map(({ key, label }) => {
                    const daySlots = tutor.availabilitySlots.filter((s) => s.day_of_week === key);
                    const isWeekend = key === 6 || key === 0;

                    return (
                      <div
                        key={key}
                        className={`p-2.5 rounded-lg border text-center transition ${
                          daySlots.length > 0
                            ? isWeekend
                              ? "border-violet-200 bg-violet-50/50"
                              : "border-slate-200 bg-slate-50"
                            : "border-slate-200 bg-slate-50/50 opacity-60"
                        }`}
                      >
                        <div
                          className={`text-xs font-bold mb-1.5 ${
                            daySlots.length > 0
                              ? isWeekend
                                ? "text-violet-700"
                                : "text-slate-700"
                              : "text-slate-400"
                          }`}
                        >
                          {label}
                        </div>
                        {daySlots.length > 0 ? (
                          <div className="space-y-1.5">
                            {daySlots.map((slot, sIdx) => (
                              <div
                                key={sIdx}
                                className={`py-1.5 px-1 rounded flex flex-col items-center justify-center font-mono text-xs font-semibold leading-tight border ${
                                  isWeekend
                                    ? "bg-emerald-100/80 text-emerald-900 border-emerald-200"
                                    : "bg-violet-100/80 text-violet-900 border-violet-200"
                                }`}
                                title={`${slot.start_time.slice(0, 5)} to ${slot.end_time.slice(0, 5)}`}
                              >
                                <span>{slot.start_time.slice(0, 5)}</span>
                                <span
                                  className={`text-[10px] font-sans font-medium my-0.5 leading-none ${
                                    isWeekend ? "text-emerald-700/70" : "text-violet-700/70"
                                  }`}
                                >
                                  to
                                </span>
                                <span>{slot.end_time.slice(0, 5)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400 italic block py-2">Off</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-center">
                  <p className="text-xs text-slate-600 font-medium">{tutor.availability}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column: Administrative Command Deck (4 cols) */}
        <div className="lg:col-span-4 space-y-6">
          {/* Verification & Action Center */}
          <div className="bg-white rounded-xl border-2 border-violet-200 shadow-md p-5 space-y-5">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs font-bold tracking-wider uppercase text-slate-400">
                  Application Lifecycle
                </span>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-bold border ${getStatusBadgeStyle(
                    tutor.status
                  )}`}
                >
                  {tutor.statusLabel}
                </span>
              </div>
              <h2 className="text-base font-bold text-slate-900">Verification Decision</h2>
              <p className="text-xs text-slate-500 mt-1">
                Review credentials and manage visibility across the rfLearn student catalog.
              </p>
            </div>

            {/* Stepper Progress Tracker */}
            <div className="space-y-2 py-2 border-y border-slate-100">
              {/* Step 1: Onboarding Submitted */}
              <div className="flex items-center gap-3 text-xs">
                <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">
                  <Check className="w-3 h-3 stroke-[3]" />
                </span>
                <span className="font-medium text-slate-700">1. Onboarding Submitted</span>
                <span className="ml-auto text-slate-400 font-mono text-[11px]">
                  {formatDisplayDate(tutor.createdAt)}
                </span>
              </div>

              {/* Step 2: Document & CV Verification */}
              <div className="flex items-center gap-3 text-xs">
                {tutor.status === "submitted" ? (
                  <>
                    <span className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center font-bold text-[10px]">
                      2
                    </span>
                    <span className="font-semibold text-slate-900">2. Document & CV Review</span>
                    <span className="ml-auto text-amber-600 font-medium text-[11px]">Awaiting</span>
                  </>
                ) : tutor.status === "rejected" ? (
                  <>
                    <span className="w-5 h-5 rounded-full bg-rose-500 text-white flex items-center justify-center font-bold text-[10px]">
                      ✕
                    </span>
                    <span className="font-semibold text-rose-700">2. Review Flagged / Rejected</span>
                  </>
                ) : (
                  <>
                    <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">
                      <Check className="w-3 h-3 stroke-[3]" />
                    </span>
                    <span className="font-medium text-slate-700">2. Document & CV Verified</span>
                    <span className="ml-auto text-emerald-600 font-medium text-[11px]">Passed</span>
                  </>
                )}
              </div>

              {/* Step 3: Admin Approval */}
              <div
                className={`flex items-center gap-3 text-xs ${
                  tutor.status === "approved" || tutor.status === "active" ? "" : "opacity-60"
                }`}
              >
                {tutor.status === "approved" || tutor.status === "active" ? (
                  <span className="w-5 h-5 rounded-full bg-emerald-500 text-white flex items-center justify-center font-bold text-[10px]">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </span>
                ) : (
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px]">
                    3
                  </span>
                )}
                <span
                  className={
                    tutor.status === "approved" || tutor.status === "active"
                      ? "font-medium text-slate-700"
                      : "text-slate-600"
                  }
                >
                  3. Admin Approval & Rate Lock
                </span>
                {tutor.status === "approved" && (
                  <span className="ml-auto text-blue-600 font-medium text-[11px]">Approved</span>
                )}
              </div>

              {/* Step 4: Live Active */}
              <div
                className={`flex items-center gap-3 text-xs ${
                  tutor.status === "active" ? "" : "opacity-50"
                }`}
              >
                {tutor.status === "active" ? (
                  <span className="w-5 h-5 rounded-full bg-violet-600 text-white flex items-center justify-center font-bold text-[10px]">
                    <Check className="w-3 h-3 stroke-[3]" />
                  </span>
                ) : (
                  <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 flex items-center justify-center font-bold text-[10px]">
                    4
                  </span>
                )}
                <span
                  className={tutor.status === "active" ? "font-semibold text-violet-800" : "text-slate-600"}
                >
                  4. Live Active in Student Catalog
                </span>
                {tutor.status === "active" && (
                  <span className="ml-auto text-emerald-600 font-semibold text-[11px]">Live Now</span>
                )}
              </div>
            </div>

            {/* Contextual Action Buttons */}
            <div className="space-y-2.5 pt-1">
              {tutor.status === "submitted" && (
                <Button
                  onClick={() => handleUpdateStatus("under_review")}
                  disabled={updatingStatus}
                  className="w-full py-2.5 px-4 rounded-lg bg-amber-600 hover:bg-amber-700 text-white font-semibold text-xs tracking-wide shadow-xs transition flex items-center justify-center gap-2"
                >
                  <Clock className="w-4 h-4" />
                  Mark Under Review
                </Button>
              )}

              {tutor.status !== "approved" && tutor.status !== "active" && (
                <Button
                  onClick={() => handleUpdateStatus("approved")}
                  disabled={updatingStatus}
                  className="w-full py-2.5 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-xs tracking-wide shadow-xs transition flex items-center justify-center gap-2"
                >
                  <UserCheck className="w-4 h-4" />
                  Approve & Mark Ready
                </Button>
              )}

              {tutor.status === "approved" && (
                <Button
                  onClick={() => handleUpdateStatus("active")}
                  disabled={updatingStatus}
                  className="w-full py-2.5 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white font-semibold text-xs tracking-wide shadow-xs transition flex items-center justify-center gap-2"
                >
                  <Zap className="w-4 h-4" />
                  Publish Live to Students
                </Button>
              )}

              {tutor.status === "active" && (
                <Button
                  variant="outline"
                  onClick={() => handleUpdateStatus("approved")}
                  disabled={updatingStatus}
                  className="w-full py-2 px-3 rounded-lg border-amber-200 text-amber-800 hover:bg-amber-50 font-medium text-xs transition flex items-center justify-center gap-2"
                >
                  Unpublish / Pause from Catalog
                </Button>
              )}

              <div className="grid grid-cols-2 gap-2 pt-1">
                {tutor.status !== "under_review" && tutor.status !== "rejected" && (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus("under_review")}
                    disabled={updatingStatus}
                    className="py-2 px-3 rounded-lg border-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-xs transition"
                  >
                    Request Review
                  </Button>
                )}

                {tutor.status !== "rejected" ? (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus("rejected")}
                    disabled={updatingStatus}
                    className="py-2 px-3 rounded-lg border-rose-200 hover:bg-rose-50 text-rose-600 font-medium text-xs transition"
                  >
                    Reject Application
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    onClick={() => handleUpdateStatus("under_review")}
                    disabled={updatingStatus}
                    className="col-span-2 py-2 px-3 rounded-lg border-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-xs transition"
                  >
                    Re-open for Review
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Uploaded CV & Verification Document Widget */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
                Curriculum Vitae (CV)
              </h3>
              {tutor.cvFilePath ? (
                <span className="text-[11px] font-mono text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-200">
                  PDF Asset
                </span>
              ) : (
                <span className="text-[11px] text-slate-400">Not Uploaded</span>
              )}
            </div>

            {tutor.cvFilePath ? (
              <>
                <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div className="w-10 h-10 rounded-lg bg-violet-100 text-violet-700 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-semibold text-slate-900 truncate" title={getFileName(tutor.cvFilePath)}>
                      {getFileName(tutor.cvFilePath)}
                    </p>
                    <p className="text-[11px] text-slate-500">Verified storage asset</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleViewCV}
                    className="py-2 px-3 rounded-lg border-slate-200 hover:bg-slate-50 text-slate-700 font-medium text-xs flex items-center justify-center gap-1.5 transition"
                  >
                    <Eye className="w-3.5 h-3.5" />
                    Preview CV
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    onClick={handleDownloadCV}
                    className="py-2 px-3 rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-medium text-xs flex items-center justify-center gap-1.5 transition"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download
                  </Button>
                </div>
              </>
            ) : (
              <div className="p-4 rounded-lg bg-slate-50 border border-slate-200 text-center">
                <p className="text-xs text-slate-500">No CV document on file.</p>
              </div>
            )}
          </div>

          {/* Billing & Platform Telemetry */}
          <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-xs space-y-4">
            <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider">
              Billing & Performance
            </h3>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-100">
                <span className="text-xs text-slate-600 font-medium">Hourly Billing Rate</span>
                <span className="text-base font-bold text-slate-900">
                  ${tutor.hourlyRate.toFixed(2)}{" "}
                  <span className="text-xs font-normal text-slate-500">/ hr</span>
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="text-xs text-slate-500">Total Sessions</div>
                  <div className="text-lg font-bold text-slate-900 mt-1">{tutor.totalSessions}</div>
                  <div className="text-[11px] text-emerald-600 font-medium">Completed</div>
                </div>
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-100">
                  <div className="text-xs text-slate-500">Active Students</div>
                  <div className="text-lg font-bold text-slate-900 mt-1">{tutor.totalStudents}</div>
                  <div className="text-[11px] text-violet-600 font-medium">Taught</div>
                </div>
              </div>
            </div>
          </div>

          {/* System Audit Metadata */}
          <div className="p-4 rounded-xl bg-slate-50 border border-slate-200 text-xs text-slate-500 space-y-2">
            <div className="flex items-center justify-between">
              <span>Record ID:</span>
              <span className="font-mono text-slate-700 text-[11px] truncate max-w-[160px]" title={tutor.id}>
                {tutor.id}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>Last Modified:</span>
              <span className="text-slate-700 font-medium">{formatDisplayDate(tutor.lastActive)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span>Storage Bucket:</span>
              <span className="font-mono text-slate-700">{CV_BUCKET}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTutor;
